/**
 * Client-side helpers for consuming the portal's site-config endpoint.
 * The site-config snapshot is written by POST /api/webhooks/wordpress/site-config
 * and read by GET /api/site-config.
 */

export interface SiteConfigFooterLink {
  label: string;
  href: string;
}

export interface SiteConfigPayload {
  navigation?: { footer?: SiteConfigFooterLink[] };
  mapUrl?: string | null;
  privacyPolicyUrl?: string | null;
}

/**
 * Extracts the Interactive Map href from a site-config payload.
 * Priority:
 *  1. `navigation.footer[].href` where label === "Interactive Map"
 *  2. Top-level `mapUrl`
 */
export function extractMapHref(payload: SiteConfigPayload | null): string | null {
  if (!payload) return null;
  const footerLinks = payload.navigation?.footer;
  if (Array.isArray(footerLinks)) {
    const match = footerLinks.find((link) => link.label === "Interactive Map");
    if (match?.href) return match.href;
  }
  return payload.mapUrl ?? null;
}

/**
 * Extracts the Privacy Policy href from a site-config payload.
 * Priority:
 *  1. `navigation.footer[].href` where label === "Privacy Policy"
 *  2. Top-level `privacyPolicyUrl`
 */
export function extractPrivacyPolicyHref(payload: SiteConfigPayload | null): string | null {
  if (!payload) return null;
  const footerLinks = payload.navigation?.footer;
  if (Array.isArray(footerLinks)) {
    const match = footerLinks.find((link) => link.label === "Privacy Policy");
    if (match?.href) return match.href;
  }
  return payload.privacyPolicyUrl ?? null;
}
